/**
 * Chemical Models (Admin)
 *
 * Interfaces for ADN Recommended Chemicals management in admin portal.
 * Matches backend Chemical model fields from hub/src/data-models/chemical.ts
 */

// ============================================
// CHEMICAL CATEGORY
// ============================================

export type ChemicalCategory =
  | 'herbicide'
  | 'insecticide'
  | 'fungicide'
  | 'fertilizer'
  | 'adjuvant'
  | 'igr'
  | 'miticide'
  | 'pgr'
  | 'other';

// ============================================
// SIGNAL WORD
// ============================================

export type SignalWord = 'CAUTION' | 'WARNING' | 'DANGER';

// ============================================
// CHEMICAL
// ============================================

export interface Chemical {
  id: string;

  // Identity
  name: string;
  epaNumber: string | null;
  manufacturer: string | null;

  // Active Ingredient
  activeIngredient: string | null;

  // Classification
  signalWord: string | null;
  category: string | null;
  formulation: string | null;

  // Safety Intervals
  rei: string | null;
  phi: string | null;

  // Application
  defaultRate: string | null;
  targetPests: string[] | null;
  crops: string[] | null;

  // EPA-specific fields (from PPLS enrichment)
  sites: string | null;  // Sites/crops from EPA (semicolon-delimited)
  pests: string | null;  // Pests from EPA (semicolon-delimited)
  pesticideType: string | null;  // Product types from EPA
  epaAltBrandNames: string | null;  // Alternate brand names from EPA

  // Source
  source: string;

  // Sponsor/Partner Info (for ADN-recommended)
  sponsorName: string | null;
  sponsorLogoUrl: string | null;
  sponsorWebsite: string | null;
  sponsorPhone: string | null;
  sponsorEmail: string | null;
  isFeatured: boolean;
  specialOffer: string | null;

  // Sponsor normalization
  sponsorId: string | null;
  compareTo: string | null;
  sponsorMetadata: any | null;

  // RUP
  rupFlag: boolean;

  // Admin-only fields
  sdsUrl: string | null;
  productUrl: string | null;

  // Metadata
  createdAt: string;
  updatedAt: string;

  // Soft delete fields
  archivedAt: string | null;
  archivedBy: string | null;

  // Computed/derived fields
  currentLabelUrl: string | null;
  currentLabels: Array<{url: string; name: string | null}> | null;

  // Related data (included when fetching single chemical)
  labels?: ChemicalLabel[];
}

// ============================================
// CHEMICAL LABEL
// ============================================

export interface ChemicalLabel {
  id: string;
  chemicalId: string;
  labelUrl: string;
  labelName: string | null;
  acceptedDate: string | null;
  isCurrent: boolean;
  createdAt: string;
  syncedAt: string | null;
}

// ============================================
// LIST FILTERS
// ============================================

export interface ChemicalFilters {
  page?: number;
  limit?: number;
  query?: string;
  category?: ChemicalCategory | string;
  signalWord?: SignalWord | string;
  featured?: boolean;
  sponsor?: boolean;
}

// ============================================
// PAGINATED RESPONSE
// ============================================

export interface PaginatedChemicalsResponse {
  chemicals: Chemical[];
  total: number;
  page: number;
  pageSize: number;
}

// ============================================
// IMPORT RESPONSE
// ============================================

export interface ChemicalImportResponse {
  imported: number;
  skipped: number;
  errors: string[];
  importedIds?: string[];
  enrichment?: {
    matched: number;
    unmatched: number;
  } | null;
}

// ============================================
// LOGO UPLOAD RESPONSE
// ============================================

export interface LogoUploadUrlResponse {
  uploadUrl: string;
  fileUrl: string;
}

// ============================================
// CREATE/UPDATE REQUEST
// ============================================

export interface ChemicalCreateRequest {
  name: string;
  category: ChemicalCategory | string;
  signalWord?: SignalWord | string | null;
  epaNumber?: string | null;
  manufacturer?: string | null;
  activeIngredient?: string | null;
  formulation?: string | null;
  rei?: string | null;
  phi?: string | null;
  defaultRate?: string | null;
  targetPests?: string[] | null;
  crops?: string[] | null;
  sponsorId?: string | null;
  sponsorName?: string | null;
  sponsorLogoUrl?: string | null;
  sponsorWebsite?: string | null;
  sponsorPhone?: string | null;
  sponsorEmail?: string | null;
  isFeatured?: boolean;
  specialOffer?: string | null;
  compareTo?: string | null;
  sponsorMetadata?: any | null;
  rupFlag?: boolean;
  sdsUrl?: string | null;
  productUrl?: string | null;
}

export interface ChemicalUpdateRequest {
  name?: string;
  category?: ChemicalCategory | string;
  signalWord?: SignalWord | string | null;
  epaNumber?: string | null;
  manufacturer?: string | null;
  activeIngredient?: string | null;
  formulation?: string | null;
  rei?: string | null;
  phi?: string | null;
  defaultRate?: string | null;
  targetPests?: string[] | null;
  crops?: string[] | null;
  sponsorId?: string | null;
  sponsorName?: string | null;
  sponsorLogoUrl?: string | null;
  sponsorWebsite?: string | null;
  sponsorPhone?: string | null;
  sponsorEmail?: string | null;
  isFeatured?: boolean;
  specialOffer?: string | null;
  compareTo?: string | null;
  sponsorMetadata?: any | null;
  rupFlag?: boolean;
  sdsUrl?: string | null;
  productUrl?: string | null;
}

// ============================================
// CSV IMPORT ROW
// ============================================

export interface ChemicalImportRow {
  name: string;
  epaNumber?: string;
  manufacturer?: string;
  activeIngredient?: string;
  signalWord?: string;  // Optional - may be populated from EPA enrichment
  rei?: string;
  phi?: string;
  category: string;
  formulation?: string;
  defaultRate?: string;
  targetPests?: string;
  crops?: string;
  compareTo?: string;
  rupFlag?: string | boolean;
  sponsor?: string;  // Sponsor name for lookup/create
  sponsorName?: string;
  sponsorWebsite?: string;
  sponsorPhone?: string;
  sponsorEmail?: string;
  isFeatured?: string | boolean;
  specialOffer?: string;
}

// ============================================
// ASYNC IMPORT JOB RESPONSE
// ============================================

export interface ChemicalImportJobResponse {
  jobId: string;
  status: 'queued';
  message: string;
  totalRows: number;
}

// ============================================
// WEBSOCKET IMPORT EVENTS
// ============================================

export interface ChemicalImportProgressEvent {
  jobId: string;
  progress: number;
  status: 'importing' | 'enriching';
  imported: number;
  total: number;
  currentRow?: string;
}

export interface ChemicalImportCompleteEvent {
  jobId: string;
  status: 'complete';
  imported: number;
  skipped: number;
  errors: string[];
  enrichment?: {
    matched: number;
    unmatched: number;
  };
}

export interface ChemicalImportFailedEvent {
  jobId: string;
  status: 'failed';
  error: string;
}
